import { useCallback, useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Warehouse, Van, PencilSimple, Trash, Plus } from "@phosphor-icons/react";
import { money, Spinner } from "./stockUtils";

const EMPTY = { name: "", type: "store", operative_id: "", notes: "" };

export function LocationsTab({ options, refreshKey, onChanged, canEdit, openAdd, onAddHandled }) {
  const [locs, setLocs] = useState(null);
  const [form, setForm] = useState(null);
  const load = useCallback(() => API.get("/stock/locations").then((r) => setLocs(r.data.locations)).catch(() => setLocs([])), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { if (openAdd) { setForm({ ...EMPTY }); onAddHandled?.(); } }, [openAdd, onAddHandled]);

  const remove = async (l) => {
    if (!window.confirm(`Remove "${l.name}"? Stock must already be moved out.`)) return;
    try { await API.delete(`/stock/locations/${l.id}`); toast.success("Location removed"); load(); onChanged?.(); } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't remove"); }
  };

  if (locs === null) return <Spinner />;
  const stores = locs.filter((l) => l.type !== "van"), vans = locs.filter((l) => l.type === "van");
  return (
    <div className="space-y-5" data-testid="stock-locations-tab">
      {locs.length === 0 && (
        <div className="text-center py-14 border border-dashed border-border rounded-lg" data-testid="locations-empty">
          <Warehouse size={36} className="mx-auto text-muted-foreground mb-2" />
          <p className="font-semibold text-sm">No stock locations yet</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">Create a main store and one van per operative. Parts are received into a store and transferred onto vans; operatives issue from their van on site.</p>
          {canEdit && <Button size="sm" className="mt-4" onClick={() => setForm({ ...EMPTY })} data-testid="locations-empty-add"><Plus size={14} className="mr-1" /> Add location</Button>}
        </div>
      )}
      {[["Stores", stores, Warehouse], ["Vans", vans, Van]].map(([title, rows, Icon]) => rows.length > 0 && (
        <div key={title}>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">{title}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {rows.map((l) => (
              <div key={l.id} className="border border-border rounded-lg bg-card p-4 flex items-start gap-3" data-testid={`location-card-${l.id}`}>
                <span className="h-9 w-9 rounded-md bg-muted flex items-center justify-center shrink-0"><Icon size={18} /></span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm leading-tight">{l.name}</p>
                  <p className="text-[11px] text-muted-foreground">{l.type === "van" ? (l.operative_name ? `Driven by ${l.operative_name}` : "No operative assigned") : "Store"}{l.notes ? ` · ${l.notes}` : ""}</p>
                  <p className="text-xs mt-2"><b>{l.part_lines}</b> part line{l.part_lines === 1 ? "" : "s"} · <span className="font-mono">{money(l.stock_value)}</span></p>
                </div>
                {canEdit && <div className="flex gap-0.5"><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setForm({ id: l.id, name: l.name, type: l.type, operative_id: l.operative_id || "", notes: l.notes || "" })} data-testid={`location-edit-${l.id}`}><PencilSimple size={14} /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => remove(l)} data-testid={`location-delete-${l.id}`}><Trash size={14} /></Button></div>}
              </div>
            ))}
          </div>
        </div>
      ))}
      <LocationFormDialog form={form} operatives={options?.operatives || []} onClose={() => setForm(null)} onSaved={() => { load(); onChanged?.(); }} />
    </div>
  );
}

function LocationFormDialog({ form, operatives, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (form) setF(form); }, [form]);
  if (!form) return null;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (f.id) await API.put(`/stock/locations/${f.id}`, f); else await API.post("/stock/locations", f);
      toast.success(f.id ? "Location updated" : "Location added");
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't save"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="location-form-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Edit location" : "New stock location"}</DialogTitle><DialogDescription>A store is a fixed stockroom; a van belongs to one operative who issues parts from it on site.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="flex gap-1.5">{[["store", "Store", Warehouse], ["van", "Van", Van]].map(([v, l, I]) => <button key={v} type="button" onClick={() => set("type", v)} className={`flex-1 rounded-md border p-2.5 text-sm flex items-center justify-center gap-2 ${f.type === v ? "border-primary bg-primary/5" : "border-border hover:bg-muted"}`} data-testid={`location-type-${v}`}><I size={16} /> {l}</button>)}</div>
          <div><Label>Name *</Label><Input required value={f.name} onChange={(e) => set("name", e.target.value)} placeholder={f.type === "van" ? "e.g. Van 3 — Jay" : "e.g. Main store"} data-testid="location-name" /></div>
          {f.type === "van" && <div><Label>Operative</Label>
            <Select value={f.operative_id || "none"} onValueChange={(v) => set("operative_id", v === "none" ? "" : v)}><SelectTrigger data-testid="location-operative"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Unassigned</SelectItem>{operatives.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select></div>}
          <div><Label>Notes</Label><Input value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Shelf layout, access, registration…" data-testid="location-notes" /></div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving} data-testid="location-save">{saving ? "Saving…" : "Save"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
